import React, { useEffect, useMemo, useState } from "react";
import { isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HeartStraightIcon as HeartStraight, HeartIcon as Heart, GenderMaleIcon as GenderMale, GenderFemaleIcon as GenderFemale, DogIcon as DogIcon, UsersFourIcon as UsersFour, FlameIcon as Flame, SparkleIcon as Sparkle, ShieldCheckIcon as ShieldCheck, MagnifyingGlassIcon as MagnifyingGlass, XIcon as CloseIcon, BellIcon as Bell, TrashIcon as Trash, CalendarIcon as Calendar, GitBranchIcon as GitBranch, MapPinIcon as MapPin } from "@phosphor-icons/react";

import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatDate, thumbUrl, formatApiError } from "@/lib/utils-app";
import VerifiedStandardsBadge from "@/components/VerifiedStandardsBadge";

/**
 * Find Mates · v2
 *
 * Layered features (each works independently — degrades gracefully):
 *   1. Match-score ring per card when a "your dog" is selected
 *   2. AI / score-driven recommendations carousel
 *   3. Heat planner subview (calendar of next windows + eligible studs)
 *   4. Filters: sex, breed, color, age, fee range, available now,
 *      verified-standards-only, distance, has video, has health certs
 *   5. Sort: newest, match desc, fee asc/desc, distance asc, soonest
 *   6. Verified Standards badge on every card
 *   7. Watchlist heart on every card
 *   8. Saved searches (list + add + delete + alert toggle)
 *   9. Side-by-side compare (up to 3)
 *   10. Improved empty state
 *   11. Quick-reply templates in interest dialog
 */
export default function Breeding() {
  const { user } = useAuth() || {};
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myDogs, setMyDogs] = useState([]);
  const [myDogId, setMyDogId] = useState("");
  const [recommendations, setRecommendations] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    sex: "",
    breed: "",
    color: "",
    age_min: "",
    age_max: "",
    fee_min: "",
    fee_max: "",
    available_now: false,
    has_video: false,
    has_health_certs: false,
    verified_standards_only: false,
    distance_miles: "",
    sort: "newest",
  });
  const [view, setView] = useState("directory"); // directory | planner | watchlist | saved
  const [savedSearches, setSavedSearches] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [compareIds, setCompareIds] = useState([]); // up to 3 listing ids
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  // Hydrate user's dogs once.
  useEffect(() => {
    api.get("/dogs").then((r) => setMyDogs(r.data || [])).catch(() => setMyDogs([]));
  }, []);

  // Load listings when filters change.
  const load = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false || v === null || v === undefined) return;
      params[k] = v;
    });
    if (myDogId) params.my_dog_id = myDogId;
    setLoading(true);
    api
      .get("/breeding", { params })
      .then((r) => setListings(r.data || []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, myDogId]);

  // Recommendations when myDogId selected.
  useEffect(() => {
    if (!myDogId) { setRecommendations([]); return; }
    api.get(`/breeding/recommendations?my_dog_id=${myDogId}&limit=8`)
      .then((r) => setRecommendations(r.data?.results || []))
      .catch(() => setRecommendations([]));
  }, [myDogId]);

  const refreshSaved = () =>
    api.get("/breeding/saved-searches").then((r) => setSavedSearches(r.data?.searches || [])).catch(() => {});
  const refreshWatchlist = () =>
    api.get("/breeding/watchlist").then((r) => setWatchlist(r.data?.listings || [])).catch(() => {});
  useEffect(() => { refreshSaved(); refreshWatchlist(); }, []);

  const toggleWatch = async (listingId, currentlyWatching) => {
    try {
      if (currentlyWatching) {
        await api.delete(`/breeding/watchlist/${listingId}`);
      } else {
        await api.post(`/breeding/watchlist/${listingId}`, {});
      }
      load();
      refreshWatchlist();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleCompare = (listingId) => {
    setCompareIds((prev) => {
      if (prev.includes(listingId)) return prev.filter((id) => id !== listingId);
      if (prev.length >= 3) {
        toast.info("Compare up to 3 listings at a time");
        return prev;
      }
      return [...prev, listingId];
    });
  };

  const cardLookup = useMemo(() => {
    const map = new Map();
    [...listings, ...recommendations, ...watchlist].forEach((l) => map.set(l.id, l));
    return map;
  }, [listings, recommendations, watchlist]);

  const visibleListings = view === "watchlist" ? watchlist : listings;

  return (
    <AppShell>
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2 flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" /> For breeders · studs &amp; dams
          </p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">Breeding Matchmaker</h1>
          <p className="text-[#6B6B6B] mt-2 max-w-2xl">
            Find a stud or dam for your breeding program. Browse listings or pick one
            of your dogs to see ranked matches with predicted COI, distance, and trust
            signals. Looking to bring a dog home instead?{" "}
            <Link to="/marketplace" className="underline decoration-gold underline-offset-2 hover:text-ink" data-testid="breeding-to-marketplace-link">
              Browse the Dog Marketplace →
            </Link>
          </p>
        </div>
        <Link to="/breeding/my">
          <Button variant="outline" className="rounded-full border-[#E5E5E5]" data-testid="my-listings-link">
            My listings
          </Button>
        </Link>
      </div>

      {/* Your-dog selector — biggest unlock */}
      {myDogs.length > 0 && (
        <div className="mb-6 rounded-2xl border border-[#C9A227]/30 bg-[#FBF7EC] p-4 sm:p-5" data-testid="my-dog-picker">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <DogIcon size={18} weight="duotone" className="text-[#8A6A00] flex-shrink-0" />
            <p className="text-[12px] font-semibold text-[#8A6A00] flex-1">
              Pick one of your dogs to see <strong>match scores, distance, and predicted COI</strong> on every listing.
            </p>
            <select
              value={myDogId}
              onChange={(e) => setMyDogId(e.target.value)}
              className="rounded-full border border-[#E8D9A6] bg-white px-4 h-10 text-sm w-full sm:w-auto"
              data-testid="my-dog-select"
            >
              <option value="">— No dog selected —</option>
              {myDogs.map((d) => (
                <option key={d.id} value={d.id}>{d.name} · {d.breed} · {d.sex}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-[#E5E5E5] mb-6 flex gap-2 overflow-x-auto" data-testid="findmates-tabs">
        {[
          ["directory", "Directory", null],
          ["planner", "Plan a litter", null],
          ["watchlist", "Watchlist", watchlist.length],
          ["saved", "Saved searches", savedSearches.length],
        ].map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            onClick={() => setView(id)}
            data-testid={`fm-tab-${id}`}
            className={`relative h-10 px-4 text-[13px] font-semibold transition-colors flex items-center gap-1.5 ${
              view === id ? "text-[#0E0E0E]" : "text-[#6B6B6B] hover:text-[#0E0E0E]"
            }`}
          >
            {label}
            {count > 0 && (
              <span className="text-[10px] bg-[#C9A227] text-white rounded-full px-2 py-0.5 font-bold">
                {count}
              </span>
            )}
            {view === id && <span className="absolute bottom-0 inset-x-3 h-0.5 bg-[#C9A227]" />}
          </button>
        ))}
      </div>

      {view === "directory" && (
        <>
          {/* Recommendations carousel */}
          {myDogId && recommendations.length > 0 && (
            <RecommendationsRow
              recs={recommendations}
              onOpen={setSelected}
              onWatch={toggleWatch}
            />
          )}

          {/* Filter bar */}
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            onSaveSearch={async (label, alert_email) => {
              try {
                await api.post("/breeding/saved-searches", { label, filters, alert_email });
                toast.success("Search saved");
                refreshSaved();
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
            myDogSelected={!!myDogId}
          />

          {/* Compare bar */}
          {compareIds.length > 0 && (
            <CompareBar
              ids={compareIds}
              cards={compareIds.map((id) => cardLookup.get(id)).filter(Boolean)}
              onClear={() => setCompareIds([])}
              onRemove={(id) => setCompareIds((p) => p.filter((x) => x !== id))}
            />
          )}

          {loading ? (
            <div className="text-[#6B6B6B]">Loading…</div>
          ) : visibleListings.length === 0 ? (
            <EmptyDirectory
              hasFilters={Object.values(filters).some((v) => v !== "" && v !== false && v !== "newest")}
              onResetFilters={() => setFilters({
                sex: "", breed: "", color: "", age_min: "", age_max: "",
                fee_min: "", fee_max: "", available_now: false, has_video: false,
                has_health_certs: false, verified_standards_only: false,
                distance_miles: "", sort: "newest",
              })}
              onListDog={() => navigate("/dogs")}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="breeding-grid">
              {visibleListings.map((l) => (
                <ListingCard
                  key={l.id}
                  listing={l}
                  onOpen={() => setSelected(l)}
                  onToggleWatch={() => toggleWatch(l.id, l.watching)}
                  onToggleCompare={() => toggleCompare(l.id)}
                  isComparing={compareIds.includes(l.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {view === "planner" && (
        <HeatPlanner
          myDogs={myDogs.filter((d) => d.sex === "female")}
          onOpenListing={setSelected}
        />
      )}

      {view === "watchlist" && (
        watchlist.length === 0 ? (
          <p className="text-[#6B6B6B] text-sm" data-testid="watchlist-empty">
            Heart any listing card to save it here.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="watchlist-grid">
            {watchlist.map((l) => (
              <ListingCard
                key={l.id}
                listing={l}
                onOpen={() => setSelected(l)}
                onToggleWatch={() => toggleWatch(l.id, true)}
                onToggleCompare={() => toggleCompare(l.id)}
                isComparing={compareIds.includes(l.id)}
              />
            ))}
          </div>
        )
      )}

      {view === "saved" && (
        <SavedSearchesView
          rows={savedSearches}
          onApply={(f) => { setFilters({ ...filters, ...f }); setView("directory"); }}
          onDelete={async (id) => {
            try {
              await api.delete(`/breeding/saved-searches/${id}`);
              toast.success("Search deleted");
              refreshSaved();
            } catch (err) { toast.error(formatApiError(err)); }
          }}
        />
      )}

      <InterestDialog
        listing={selected}
        myDogs={myDogs}
        onClose={() => setSelected(null)}
        onSent={() => {
          setSelected(null);
          toast.success("Interest sent — the owner will receive a message in their inbox");
        }}
      />
    </AppShell>
  );
}

// ---------------- Recommendations row ----------------

function RecommendationsRow({ recs, onOpen, onWatch }) {
  return (
    <section className="mb-8" data-testid="recommendations-row">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
          <Sparkle size={18} weight="fill" className="text-gold" /> Top matches for you
        </h2>
        <p className="text-[11px] uppercase tracking-[0.16em] text-[#6B6B6B] font-bold">
          Ranked by COI, distance, & trust
        </p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3" style={{ scrollSnapType: "x proximity" }}>
        {recs.map((l) => (
          <div key={l.id} className="flex-shrink-0 w-[280px]" style={{ scrollSnapAlign: "start" }}>
            <ListingCard
              listing={l}
              onOpen={() => onOpen(l)}
              onToggleWatch={() => onWatch(l.id, l.watching)}
              onToggleCompare={() => {}}
              isComparing={false}
              compact
            />
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------- Filter bar ----------------

function FilterBar({ filters, setFilters, showFilters, setShowFilters, onSaveSearch, myDogSelected }) {
  const set = (k, v) => setFilters({ ...filters, [k]: v });
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-4 mb-6" data-testid="filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => set("sex", "")} className={pill(filters.sex === "")} data-testid="filter-all">All</button>
        <button onClick={() => set("sex", "male")} className={pill(filters.sex === "male", "#0E0E0E")} data-testid="filter-studs">
          <GenderMale size={14} /> Studs
        </button>
        <button onClick={() => set("sex", "female")} className={pill(filters.sex === "female", "#C1272D")} data-testid="filter-females">
          <GenderFemale size={14} /> Females
        </button>
        <Input
          placeholder="Filter by breed…"
          value={filters.breed}
          onChange={(e) => set("breed", e.target.value)}
          className="flex-1 min-w-[160px] rounded-full border-[#E5E5E5]"
          data-testid="filter-breed-input"
        />
        <select
          value={filters.sort}
          onChange={(e) => set("sort", e.target.value)}
          className="h-10 rounded-full border border-[#E5E5E5] bg-white px-4 text-sm"
          data-testid="sort-select"
        >
          <option value="newest">Newest</option>
          {myDogSelected && <option value="match_desc">Best match</option>}
          {myDogSelected && <option value="distance_asc">Closest first</option>}
          <option value="fee_asc">Lowest fee</option>
          <option value="fee_desc">Highest fee</option>
          <option value="available_soonest">Soonest available</option>
        </select>
        <Button
          variant="outline"
          onClick={() => setShowFilters((v) => !v)}
          className="rounded-full border-[#E5E5E5] h-10"
          data-testid="more-filters-btn"
        >
          <MagnifyingGlass size={14} className="mr-1.5" />
          {showFilters ? "Hide filters" : "More filters"}
        </Button>
      </div>

      {showFilters && (
        <div className="mt-4 pt-4 border-t border-[#E5E5E5] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="advanced-filters">
          <Input placeholder="Color" value={filters.color} onChange={(e) => set("color", e.target.value)} data-testid="filter-color" />
          <Input placeholder="Min age (yrs)" type="number" value={filters.age_min} onChange={(e) => set("age_min", e.target.value)} data-testid="filter-age-min" />
          <Input placeholder="Max age (yrs)" type="number" value={filters.age_max} onChange={(e) => set("age_max", e.target.value)} data-testid="filter-age-max" />
          <Input placeholder="Min fee ($)" type="number" value={filters.fee_min} onChange={(e) => set("fee_min", e.target.value)} data-testid="filter-fee-min" />
          <Input placeholder="Max fee ($)" type="number" value={filters.fee_max} onChange={(e) => set("fee_max", e.target.value)} data-testid="filter-fee-max" />
          <Input placeholder="Within X miles" type="number" value={filters.distance_miles} onChange={(e) => set("distance_miles", e.target.value)} data-testid="filter-distance" />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={filters.available_now} onChange={(e) => set("available_now", e.target.checked)} className="accent-[#C9A227]" data-testid="filter-available" />
            Available now
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={filters.has_video} onChange={(e) => set("has_video", e.target.checked)} className="accent-[#C9A227]" data-testid="filter-has-video" />
            Has video
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={filters.has_health_certs} onChange={(e) => set("has_health_certs", e.target.checked)} className="accent-[#C9A227]" data-testid="filter-health-certs" />
            Health certs uploaded
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={filters.verified_standards_only} onChange={(e) => set("verified_standards_only", e.target.checked)} className="accent-[#0F8043]" data-testid="filter-verified-standards" />
            Verified Standards only
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const label = prompt("Name this saved search:");
              if (!label) return;
              const alert_email = confirm("Email me when new matches show up?");
              onSaveSearch(label, alert_email);
            }}
            data-testid="save-search-btn"
            className="rounded-full"
          >
            <Bell size={12} className="mr-1" /> Save & alert
          </Button>
        </div>
      )}
    </div>
  );
}

const pill = (active, color = "#0E0E0E") =>
  `px-4 py-2 rounded-full text-sm flex items-center gap-1.5 transition-colors ${
    active ? `bg-[${color}] text-white` : "bg-[#FAFAFA] text-[#6B6B6B] hover:bg-[#0E0E0E]/5"
  }`;

// ---------------- Listing card ----------------

function ListingCard({ listing, onOpen, onToggleWatch, onToggleCompare, isComparing, compact = false }) {
  const d = listing.dog;
  const isFemale = d.sex === "female";
  const isElite = !!listing.elite_verified;
  const m = listing.match;
  return (
    <div
      className={`bg-white border rounded-xl overflow-hidden hover:-translate-y-[2px] transition-transform flex flex-col ${
        isComparing
          ? "border-[#C9A227] shadow-[0_8px_30px_rgba(201,162,39,0.20)]"
          : isElite
            ? "border-crimson/40 shadow-[0_8px_30px_rgba(193,39,45,0.12)]"
            : "border-[#E5E5E5] shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
      }`}
      data-testid={`listing-card-${listing.id}`}
    >
      <div className={`${compact ? "aspect-[16/10]" : "aspect-[4/3]"} bg-[#FAFAFA] relative`}>
        {d.photos?.[0] ? (
          <img src={thumbUrl(d.photos[0])} alt={d.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <DogIcon size={40} weight="duotone" className="text-[#6B6B6B]/40" />
          </div>
        )}
        <span
          className={`absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.15em] rounded-full px-2.5 py-1 flex items-center gap-1 ${
            isFemale ? "bg-[#C1272D] text-white" : "bg-[#0E0E0E] text-white"
          }`}
        >
          {isFemale ? <GenderFemale size={10} weight="bold" /> : <GenderMale size={10} weight="bold" />}
          {isFemale ? "Female" : "Stud"}
        </span>
        {isElite && (
          <span
            className="absolute top-3 left-1/2 -translate-x-1/2 text-[9.5px] font-bold uppercase tracking-wider rounded-full px-2.5 py-0.5 inline-flex items-center gap-1 bg-gradient-to-r from-[#7A1A1A] via-[#C1272D] to-[#7A1A1A] text-white shadow-[0_2px_6px_rgba(193,39,45,0.35)]"
            data-testid={`listing-elite-${listing.id}`}
            title="Elite Verified Breeder — prestige tier"
          >
            Elite Verified
          </span>
        )}
        {/* Watch heart */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleWatch(); }}
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/95 hover:bg-white border border-[#E5E5E5] flex items-center justify-center transition-colors"
          data-testid={`watch-${listing.id}`}
        >
          {listing.watching ? <Heart size={14} weight="fill" className="text-crimson" /> : <Heart size={14} className="text-[#6B6B6B]" />}
        </button>
        {/* Match-score ring (when present) */}
        {m && m.tier !== "ineligible" && (
          <div
            className={`absolute bottom-3 right-3 h-12 w-12 rounded-full border-2 flex items-center justify-center font-bold text-sm bg-white ${
              m.tier === "excellent" ? "border-[#0F8043] text-[#0F8043]"
              : m.tier === "good" ? "border-[#C9A227] text-[#8A6A00]"
              : m.tier === "caution" ? "border-[#C1272D]/60 text-[#C1272D]"
              : "border-[#6B6B6B]/40 text-[#6B6B6B]"
            }`}
            title={`Match: ${m.tier} · ${m.coi?.percent ?? 0}% COI`}
            data-testid={`match-score-${listing.id}`}
          >
            {m.score}
          </div>
        )}
      </div>
      <div className={`${compact ? "p-4" : "p-5"} flex-1 flex flex-col`}>
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-spectral text-xl tracking-tight truncate">{d.name}</div>
          {listing.verified_standards && <VerifiedStandardsBadge size="xs" />}
        </div>
        <p className="text-xs text-[#6B6B6B] mt-0.5">{d.breed}{d.color ? ` · ${d.color}` : ""}</p>
        {listing.breeder && (
          <p className="text-xs text-[#6B6B6B] mt-1 flex items-center gap-1 truncate">
            <UsersFour size={12} /> {listing.breeder.kennel_name || listing.breeder.name}
          </p>
        )}
        {m && m.distance_miles != null && (
          <p className="text-xs text-[#6B6B6B] mt-1 flex items-center gap-1">
            <MapPin size={12} /> {m.distance_miles} mi away
          </p>
        )}
        {m && m.coi && (
          <p className="text-[11px] mt-1.5 flex items-center gap-1 text-[#3F3F3F]">
            <GitBranch size={11} /> Predicted litter COI <strong className="ml-1">{m.coi.percent}%</strong>
          </p>
        )}
        {!compact && d.titles?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {d.titles.slice(0, 3).map((t) => (
              <span key={t.id} className="text-[10px] font-semibold bg-[#C1272D]/10 text-crimson border border-[#C1272D]/20 rounded-full px-2 py-0.5">
                {t.title}
              </span>
            ))}
          </div>
        )}
        {listing.next_heat && (
          <div className="mt-3 text-xs flex items-center gap-1.5 text-crimson">
            <Flame size={14} weight="duotone" />
            <span>Next heat: <span className="font-semibold">{formatDate(listing.next_heat.start_date)}</span></span>
          </div>
        )}
        {listing.fee_requested > 0 && (
          <p className="text-xs text-[#6B6B6B] mt-2">
            Stud fee: <span className="text-ink font-semibold">${listing.fee_requested.toFixed(2)}</span>
          </p>
        )}
        {!compact && listing.description && (
          <p className="text-sm text-ink mt-3 leading-relaxed line-clamp-2">{listing.description}</p>
        )}
        <div className="flex-1" />
        <div className="mt-4 grid grid-cols-3 gap-2">
          <Link to={`/d/${d.id}`}>
            <Button variant="outline" className="w-full rounded-full border-[#E5E5E5]" size="sm">View</Button>
          </Link>
          <Button
            variant="outline"
            onClick={onToggleCompare}
            className={`w-full rounded-full ${isComparing ? "border-[#C9A227] text-[#8A6A00]" : "border-[#E5E5E5]"}`}
            size="sm"
            data-testid={`compare-toggle-${listing.id}`}
          >
            {isComparing ? "Comparing" : "Compare"}
          </Button>
          <Button
            onClick={onOpen}
            className="w-full rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            size="sm"
            data-testid={`express-interest-btn-${listing.id}`}
          >
            Interest
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Interest dialog ----------------

const QUICK_REPLIES = [
  "Hi — interested in this pairing. Could you share recent OFA / genetic-panel results?",
  "When would you be available for a stud booking? I'd like to align with my female's next heat window.",
  "Could you share the contract / health-guarantee terms you typically offer?",
  "Are you open to chilled or frozen-semen shipping if we're not local?",
];

function InterestDialog({ listing, myDogs, onClose, onSent }) {
  const [myDogId, setMyDogId] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  // Credit-pack state: balance is fetched on mount; the "use credit"
  // path bypasses Stripe checkout entirely when the user has ≥1.
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (!listing) return;
    setNote("");
    setMyDogId("");
    api
      .get("/breeding/my-credit-balance", { silent: true })
      .then((r) => setCredits(r.data?.credits || { balance: 0 }))
      .catch(() => setCredits({ balance: 0 }));
  }, [listing]);

  const eligibleDogs = useMemo(() => {
    if (!listing) return [];
    const t = listing.dog.sex === "male" ? "female" : "male";
    return myDogs.filter((d) => d.sex === t);
  }, [listing, myDogs]);

  if (!listing) return null;

  // Two send paths — one for credit-spend (no Stripe round-trip), one
  // for $25 ad-hoc Stripe checkout. The dialog defaults to credit if
  // the user has any in the bank, otherwise the $25 Stripe path.
  const hasCredit = (credits?.balance || 0) >= 1;

  const sendWithCredit = async () => {
    setSending(true);
    try {
      const { data } = await api.post(
        `/breeding/${listing.id}/interest/use-credit`,
        { my_dog_id: myDogId || undefined, note },
      );
      setCredits(data?.credits || { balance: (credits?.balance || 1) - 1 });
      onSent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const sendWithStripe = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "breeding_inquiry_fee",
        breeding_listing_id: listing.id,
        breeding_my_dog_id: myDogId || undefined,
        breeding_note: note,
        origin_url: webOrigin(),
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not open Stripe checkout.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const buyCreditPack = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "breeding_inquiry_credits_3pack",
        origin_url: webOrigin(),
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not open Stripe checkout.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!listing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Express interest in {listing.dog.name}</DialogTitle>
        </DialogHeader>

        {/* Pricing transparency strip — explicit about the $25 fee +
            the auto-refund-if-ghosted policy so the inquirer doesn't
            balk at checkout time. Renders before the form so the user
            sees the economics first. */}
        <div
          className="rounded-xl border border-[#E2DAC2] bg-cream/40 p-3 text-[12px] leading-snug"
          data-testid="interest-pricing-strip"
        >
          {hasCredit ? (
            <p>
              <strong>You have {credits.balance} inquiry credit
              {credits.balance === 1 ? "" : "s"}.</strong>{" "}
              This inquiry will use 1 credit. If the breeder doesn&rsquo;t reply within
              7 days, the credit is automatically restored.
            </p>
          ) : isIOS() ? (
            <p>Direct inquiries aren&rsquo;t available in the app.</p>
          ) : (
            <p>
              <strong>$25 intent-to-breed fee.</strong> Filters tire-kickers — every
              inquiry the breeder receives is serious. <strong>Auto-refunded</strong> if
              they don&rsquo;t reply within 7 days.
            </p>
          )}
        </div>

        <div className="space-y-4">
          <p className="text-sm text-[#6B6B6B]">
            A message will land in <span className="font-semibold text-ink">{listing.owner.name}</span>&rsquo;s inbox. Contact details stay private until you both agree.
          </p>
          {eligibleDogs.length > 0 && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Pair with (optional)
              </Label>
              <select
                value={myDogId}
                onChange={(e) => setMyDogId(e.target.value)}
                className="mt-1 w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="interest-my-dog-select"
              >
                <option value="">— None / just asking —</option>
                {eligibleDogs.map((d) => (
                  <option key={d.id} value={d.id}>{d.name} · {d.breed}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Quick replies</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {QUICK_REPLIES.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setNote(note ? `${note}\n\n${q}` : q)}
                  className="text-[11px] bg-[#FAFAFA] hover:bg-[#FBF7EC] border border-[#E5E5E5] rounded-full px-3 py-1.5 text-left max-w-full"
                  data-testid={`quick-reply-${i}`}
                >
                  {q.slice(0, 60)}…
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Message</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Say hi, share your plans, propose timing…" className="mt-1" rows={5} data-testid="interest-note" />
          </div>

          {/* Save-$15 upsell: only shown when the user is paying ad-hoc
              (no credit balance yet). Quiet copy, not pushy. */}
          {!hasCredit && !isIOS() && (
            <button
              type="button"
              onClick={buyCreditPack}
              disabled={sending}
              data-testid="buy-credit-pack-btn"
              className="w-full text-left rounded-xl border border-dashed border-gold/50 bg-gold/5 hover:bg-gold/10 px-4 py-3 transition-colors disabled:opacity-60"
            >
              <p className="text-[12.5px] font-bold text-ink">
                Inquiring on more than one listing? Get the 3-pack for $60.
              </p>
              <p className="text-[11.5px] text-[#6B6B6B] mt-0.5">
                $20 per inquiry · Save $15 · Same 7-day auto-refund policy per credit.
              </p>
            </button>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          {hasCredit ? (
            <Button
              onClick={sendWithCredit}
              disabled={sending}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
              data-testid="send-interest-btn"
            >
              {sending ? "Sending…" : `Use 1 credit & send`}
            </Button>
          ) : isIOS() ? (
            <IosPurchaseNotice label="Intent-to-breed inquiry" showPlans={false} />
          ) : (
            <Button
              onClick={sendWithStripe}
              disabled={sending}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
              data-testid="send-interest-btn"
            >
              {sending ? "Opening checkout…" : "Pay $25 & send"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Compare bar ----------------

function CompareBar({ ids, cards, onClear, onRemove }) {
  return (
    <div className="mb-4 rounded-2xl border border-[#C9A227]/40 bg-[#FBF7EC] p-3 flex flex-wrap items-center gap-2" data-testid="compare-bar">
      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A6A00] mr-2">Comparing {ids.length}/3</p>
      {cards.map((c) => (
        <span key={c.id} className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1 text-[12px] border border-[#E8D9A6]">
          {c.dog.name}
          <button type="button" onClick={() => onRemove(c.id)} className="text-[#6B6B6B] hover:text-crimson" data-testid={`compare-remove-${c.id}`}>
            <CloseIcon size={11} weight="bold" />
          </button>
        </span>
      ))}
      <div className="flex-1" />
      <Button size="sm" variant="outline" onClick={onClear} className="rounded-full" data-testid="compare-clear">Clear</Button>
      {cards.length >= 2 && <CompareDialog cards={cards} />}
    </div>
  );
}

function CompareDialog({ cards }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} className="rounded-full bg-[#0E0E0E] hover:bg-[#C9A227] text-white" data-testid="compare-open">
        Compare side-by-side
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader><DialogTitle>Side-by-side</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((c) => (
              <div key={c.id} className="rounded-xl border border-[#E5E5E5] p-3" data-testid={`compare-card-${c.id}`}>
                {c.dog.photos?.[0] && (
                  <img src={thumbUrl(c.dog.photos[0])} alt={c.dog.name} className="aspect-square rounded-lg object-cover mb-3" />
                )}
                <p className="font-spectral text-lg">{c.dog.name}</p>
                <p className="text-xs text-[#6B6B6B]">{c.dog.breed} · {c.dog.color || "—"}</p>
                <ul className="text-[12px] mt-3 space-y-1">
                  <li>Sex: <strong>{c.dog.sex}</strong></li>
                  <li>DOB: <strong>{c.dog.dob || "—"}</strong></li>
                  <li>Fee: <strong>${(c.fee_requested || 0).toFixed(2)}</strong></li>
                  <li>Titles: <strong>{(c.dog.titles || []).length}</strong></li>
                  <li>Verified Standards: <strong>{c.verified_standards ? "Yes" : "No"}</strong></li>
                  {c.match && <li>Match score: <strong>{c.match.score}/100 · {c.match.tier}</strong></li>}
                  {c.match && c.match.coi && <li>Predicted COI: <strong>{c.match.coi.percent}%</strong></li>}
                  {c.match && c.match.distance_miles != null && <li>Distance: <strong>{c.match.distance_miles} mi</strong></li>}
                </ul>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------- Heat planner ----------------

function HeatPlanner({ myDogs, onOpenListing }) {
  const [selectedId, setSelectedId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedId) { setData(null); return; }
    setLoading(true);
    api.get(`/breeding/heat-planner/${selectedId}`)
      .then((r) => setData(r.data))
      .catch((err) => { toast.error(formatApiError(err)); setData(null); })
      .finally(() => setLoading(false));
  }, [selectedId]);

  if (myDogs.length === 0) {
    return (
      <div className="rounded-2xl border border-[#E5E5E5] bg-white p-8 text-center" data-testid="planner-no-dogs">
        <Calendar size={32} className="text-[#6B6B6B] mx-auto mb-3" />
        <p className="text-ink font-semibold mb-1">No registered females yet</p>
        <p className="text-[#6B6B6B] text-sm">Register a female and log her heat cycles to plan a litter.</p>
      </div>
    );
  }
  return (
    <section data-testid="heat-planner">
      <div className="mb-5 flex items-center gap-3">
        <Calendar size={18} weight="duotone" className="text-crimson" />
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="rounded-full border border-[#E5E5E5] bg-white h-10 px-4 text-sm flex-1 max-w-md"
          data-testid="planner-dog-select"
        >
          <option value="">— Pick a female —</option>
          {myDogs.map((d) => (
            <option key={d.id} value={d.id}>{d.name} · {d.breed}</option>
          ))}
        </select>
      </div>
      {loading && <p className="text-[#6B6B6B]">Calculating windows…</p>}
      {!loading && data && data.windows.length === 0 && (
        <p className="text-[#6B6B6B]">No upcoming heat windows projected.</p>
      )}
      {!loading && data && data.windows.map((w, i) => (
        <article key={i} className="mb-6 rounded-2xl border border-[#E5E5E5] bg-white p-5" data-testid={`heat-window-${i}`}>
          <div className="flex items-baseline justify-between gap-2 mb-3">
            <h3 className="font-spectral text-xl tracking-tight">
              Heat window {i + 1}
              {w.is_projected && (
                <span className="ml-2 text-[10px] uppercase tracking-[0.18em] font-bold text-gold">Projected</span>
              )}
            </h3>
            <p className="text-[12px] text-[#6B6B6B]">
              {formatDate(w.heat_start)} → {formatDate(w.heat_end)}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-[12px]">
            <Stat label="Heat starts" value={formatDate(w.heat_start)} />
            <Stat label="Heat ends" value={formatDate(w.heat_end)} />
            <Stat label="Projected whelp" value={formatDate(w.projected_whelp)} />
            <Stat label="Pups go home (~8 wks)" value={formatDate(w.projected_go_home)} />
          </div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">
            Eligible studs available in this window ({w.eligible_studs.length})
          </p>
          {w.eligible_studs.length === 0 ? (
            <p className="text-sm text-[#6B6B6B]">No active stud listings whose terms overlap this window.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {w.eligible_studs.map((s) => (
                <button
                  key={s.id}
                  onClick={() => onOpenListing(s)}
                  className="text-left rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] hover:border-[#C9A227] p-3 transition-colors"
                  data-testid={`planner-stud-${s.id}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-semibold truncate">{s.dog.name}</p>
                    {s.match && (
                      <span className="text-[11px] font-bold text-[#0F8043]">{s.match.score}/100</span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#6B6B6B] truncate">{s.dog.breed}</p>
                  {s.match?.coi && <p className="text-[11px] text-[#6B6B6B]">{s.match.coi.percent}% COI</p>}
                </button>
              ))}
            </div>
          )}
        </article>
      ))}
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6B6B6B] mb-0.5">{label}</p>
      <p className="font-semibold text-ink">{value}</p>
    </div>
  );
}

// ---------------- Saved searches view ----------------

function SavedSearchesView({ rows, onApply, onDelete }) {
  if (rows.length === 0) {
    return <p className="text-[#6B6B6B] text-sm" data-testid="saved-empty">Save a search from the filter bar to be alerted when new matches appear.</p>;
  }
  return (
    <ul className="space-y-2" data-testid="saved-searches-list">
      {rows.map((r) => (
        <li key={r.id} className="rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 flex items-center justify-between gap-3" data-testid={`saved-search-${r.id}`}>
          <div className="min-w-0">
            <p className="font-semibold truncate">{r.label}</p>
            <p className="text-[11px] text-[#6B6B6B] truncate">
              {Object.entries(r.filters || {}).filter(([, v]) => v !== "" && v !== false).map(([k, v]) => `${k}:${v}`).join(" · ") || "no filters"}
            </p>
            {r.alert_email && <p className="text-[11px] text-[#0F8043] mt-0.5"><Bell size={10} className="inline mr-1" /> Email alerts on</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => onApply(r.filters || {})} data-testid={`apply-search-${r.id}`} className="rounded-full">Apply</Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(r.id)} data-testid={`delete-search-${r.id}`} className="rounded-full border-[#C1272D] text-crimson">
              <Trash size={11} />
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------- Empty state ----------------

function EmptyDirectory({ hasFilters, onResetFilters, onListDog }) {
  return (
    <div className="text-center py-16 rounded-2xl border border-dashed border-[#E5E5E5] bg-white" data-testid="empty-directory">
      <div className="h-14 w-14 rounded-full bg-[#FBF7EC] text-gold mx-auto flex items-center justify-center mb-4">
        <HeartStraight size={26} weight="duotone" />
      </div>
      <p className="text-ink font-semibold text-lg mb-1">
        {hasFilters ? "No listings match those filters." : "No active listings yet."}
      </p>
      <p className="text-sm text-[#6B6B6B] mb-5">
        {hasFilters
          ? "Try widening the radius, clearing breed, or unchecking 'Available now'."
          : "Be the first to list one of your dogs for breeding."}
      </p>
      <div className="flex items-center justify-center gap-2">
        {hasFilters && (
          <Button onClick={onResetFilters} variant="outline" className="rounded-full" data-testid="reset-filters-btn">
            Clear filters
          </Button>
        )}
        <Button onClick={onListDog} className="rounded-full bg-[#0E0E0E] hover:bg-[#C9A227] text-white" data-testid="list-dog-btn">
          <ShieldCheck size={14} className="mr-1.5" /> Go to my dogs
        </Button>
      </div>
    </div>
  );
}
